import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useAppSelector, useAppDispatch } from "@store/hooks";
import { WorkspaceReadingsTable } from './WorkspaceReadingsTable';
import { CreateReadingButton } from './CreateReadingButton';
import { CreateReadingModal } from "@features/workspace-readings/components/CreateReadingModal";
import EmptyState from "@shared/components/states/EmptyState";
import {
  selectAllWorkspaceReadings,
  selectReadingsLoading,
  selectReadingsError,
  loadAllWorkspaceReadings,
} from "@features/workspace-readings/state/workspaceReadingsSlice";
import type { RootState } from "@store/index";

export const ReadingsTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const readings = useAppSelector(selectAllWorkspaceReadings);
  const loading = useAppSelector(selectReadingsLoading);
  const error = useAppSelector(selectReadingsError);
  const workspaceId = useAppSelector((state: RootState) => state.workspace.workspace_id);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      dispatch(loadAllWorkspaceReadings(workspaceId));
    }
  }, [workspaceId, dispatch]);

  if (loading && readings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Workspace Readings</Typography>
        <CreateReadingButton />
      </Box>

      {readings.length === 0 && !loading ? (
        <EmptyState
          icon={<DescriptionOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          title="No readings yet"
          description="Upload PDFs to annotate and extract arguments from your research materials."
          actionLabel="Add Reading"
          onAction={() => setCreateModalOpen(true)}
        />
      ) : (
        <WorkspaceReadingsTable readings={readings} />
      )}

      {workspaceId && (
        <CreateReadingModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          workspaceId={workspaceId}
        />
      )}
    </Box>
  );
};
